import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages as pages } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';
import { CtaButton } from './CtaButton';
type Position = {
  active: number;
  prev: number;
};
const AUTOPLAY_MS = 4000;
const CAPTION_SPACE = 84;
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
export function PageCarousel() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const suppressClick = useRef(false);
  const reduceMotion = useReducedMotion();
  const [width, setWidth] = useState(360);
  const [pos, setPos] = useState<Position>({
    active: 0,
    prev: 0
  });
  const [dragX, setDragX] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [touching, setTouching] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const count = pages.length;
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const goTo = useCallback((index: number) => setPos(p => ({
    active: (index % count + count) % count,
    prev: p.active
  })), [count]);
  const step = useCallback((dir: 1 | -1) => setPos(p => ({
    active: (p.active + dir + count) % count,
    prev: p.active
  })), [count]);

  // Autoplay: pauses on hover/touch, stops for good once the user interacts.
  useEffect(() => {
    if (stopped || hovered || touching || lightboxIndex !== null || reduceMotion) return;
    const timer = window.setInterval(() => step(1), AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [stopped, hovered, touching, lightboxIndex, reduceMotion, step]);
  const interact = (fn: () => void) => {
    setStopped(true);
    fn();
  };

  // Layout per breakpoint (based on the carousel's own width)
  const isDesktop = width >= 860;
  const isTablet = width >= 520 && !isDesktop;
  const gap = isDesktop ? 28 : isTablet ? 20 : 16;
  const slideW = isDesktop ? Math.round(Math.max(300, Math.min(340, (width - 2 * gap) / 3.1))) : isTablet ? Math.round(width * 0.44) : Math.round(width * 0.8);
  const imgH = Math.round(slideW * 16 / 9);
  const activeScale = isDesktop ? 1.05 : 1;
  const sideScale = isDesktop ? 0.92 : 1;
  const trackH = Math.round(imgH * activeScale) + CAPTION_SPACE;
  const circular = (i: number, from: number) => {
    let d = i - from;
    if (d > count / 2) d -= count;
    if (d < -count / 2) d += count;
    return d;
  };

  // Swipe
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.pointerType !== 'mouse') setTouching(true);
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      moved: false
    };
    suppressClick.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    if (!drag.current.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) drag.current.moved = true;
    if (drag.current.moved) setDragX(dx);
  };
  const endDrag = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') setTouching(false);
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const moved = drag.current.moved;
    drag.current = null;
    setDragX(0);
    if (!moved) return;
    suppressClick.current = true;
    if (Math.abs(dx) > 50) interact(() => step(dx < 0 ? 1 : -1));
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      interact(() => step(1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      interact(() => step(-1));
    }
  };
  const openPage = (i: number) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    interact(() => {
      goTo(i);
      setLightboxIndex(i);
    });
  };
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const changeLightbox = useCallback((i: number) => {
    setLightboxIndex(i);
    goTo(i);
  }, [goTo]);
  const arrowClass = 'flex h-12 w-12 flex-none items-center justify-center rounded-full bg-willow text-willow-ink shadow-card transition-colors duration-150 hover:bg-willow-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cream/70';
  const prevButton = (className: string) => <button type="button" onClick={() => interact(() => step(-1))} aria-label="Previous page" className={`${arrowClass} ${className}`} data-id="element-184" data-mp="33m4dt5psq3aq">
      <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-185" data-mp="3cnltwo37e4zf" />
    </button>;
  const nextButton = (className: string) => <button type="button" onClick={() => interact(() => step(1))} aria-label="Next page" className={`${arrowClass} ${className}`} data-id="element-186" data-mp="110ijfzkwagip">
      <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-187" data-mp="vzm8j224lu3s" />
    </button>;
  return <section aria-labelledby="pages-heading" className="w-full overflow-hidden border-y-4 border-clay bg-field" data-id="element-188" data-mp="s03wkvsa8td3">
      <div className="mx-auto max-w-6xl py-16 sm:py-20" data-id="element-189" data-mp="n2bsz4i9zit2">
        <div className="px-5 text-center" data-id="element-190" data-mp="1jqzyx2gpvfm4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-willow" data-id="element-191" data-mp="1jsuk8hvwh9qq">Look inside · Volume 1</p>
          <h2 id="pages-heading" className="mt-3 font-display text-3xl text-cream sm:text-5xl" data-id="element-192" data-mp="1fritg91aiigy">
            Every session, planned and ready to run
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-cream/75" data-id="element-193" data-mp="15ytuuequ8z31">
            Real pages from Volume 1, Set Up the Squad. Each session comes as two pages: a Plan page (stations,
            distances, timings) and a Run It page (the game, the coaching cues, the scoring).
          </p>
        </div>

        <div role="region" aria-roledescription="carousel" aria-label="Pages from Volume 1" tabIndex={0} onKeyDown={onKeyDown} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className="mt-10 flex items-center gap-4 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-willow sm:mt-12 lg:px-5" data-id="element-194" data-mp="2hkst1a4druz3">
          {prevButton('hidden lg:flex')}

          <div ref={viewportRef} className="relative min-w-0 flex-1 touch-pan-y select-none" style={{
          height: trackH
        }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onPointerLeave={e => drag.current && endDrag(e)} data-id="element-195" data-mp="23tns84pa0wn6">
            <p className="sr-only" aria-live={stopped ? 'polite' : 'off'} data-id="element-196" data-mp="3o7odzldjvzri">
              Page {pos.active + 1} of {count}: {pages[pos.active].caption}
            </p>
            {pages.map((page, i) => {
            const offset = circular(i, pos.active);
            const prevOffset = circular(i, pos.prev);
            const teleport = Math.abs(offset - prevOffset) > 2;
            const isActive = offset === 0;
            const visible = Math.abs(offset) <= 1;
            const scale = isActive ? activeScale : sideScale;
            const x = offset * (slideW + gap) + dragX;
            const animate = !teleport && dragX === 0 && !reduceMotion;
            return <div key={page.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} aria-hidden={!visible} className="absolute left-1/2 top-0" style={{
              width: slideW,
              transform: `translateX(calc(-50% + ${x}px)) scale(${scale})`,
              transformOrigin: 'top center',
              opacity: visible ? 1 : 0,
              zIndex: isActive ? 2 : 1,
              transition: animate ? `transform 300ms ${EASE}, opacity 250ms ${EASE}` : 'none',
              pointerEvents: visible ? 'auto' : 'none'
            }} data-id="element-197" data-mp="25aa08y0x5p6l" data-mpi={i}>
                  <button type="button" tabIndex={visible ? 0 : -1} onClick={() => openPage(i)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-2xl bg-white shadow-book focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-willow focus-visible:ring-offset-2 focus-visible:ring-offset-field" data-id="element-198" data-mp="2y7rp3qqgmio5">
                    <img src={page.src} alt={page.alt} width={1152} height={2048} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} className="block w-full object-contain" style={{
                  height: imgH
                }} data-id="element-199" data-mp="mkbifwxjye5o" />
                  </button>
                  <p className={`mx-auto mt-4 max-w-[30ch] text-center text-sm leading-relaxed transition-colors duration-200 ${isActive ? 'text-cream' : 'text-cream/50'}`} data-id="element-200" data-mp="1xaga0gwpjosp">
                    {page.caption}
                  </p>
                </div>;
          })}
          </div>

          {nextButton('hidden lg:flex')}
        </div>

        <div className="mt-6 flex items-center justify-center gap-5 px-5" data-id="element-201" data-mp="1ecnjyf76n9cq">
          {prevButton('lg:hidden')}
          <div className="flex items-center gap-1.5" role="group" aria-label="Choose a page" data-id="element-202" data-mp="3dnexpk2swlwn">
            {pages.map((page, i) => <button key={page.src} type="button" onClick={() => interact(() => goTo(i))} aria-label={`Go to page ${i + 1}: ${page.caption}`} aria-current={pos.active === i ? 'true' : undefined} className="flex h-8 items-center justify-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-willow" data-id="element-203" data-mp="38nsi53zi2tko" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ${pos.active === i ? 'w-6 bg-willow' : 'w-2 bg-white/30'}`} data-id="element-204" data-mp="khf5qgy3vk65" />
              </button>)}
          </div>
          {nextButton('lg:hidden')}
        </div>

        <p className="mt-6 px-5 text-center text-[13px] text-cream/60" data-id="element-205" data-mp="pddnvmsuc5y4">Tap any page to see it full size.</p>

        <div className="mx-auto mt-8 max-w-md px-5" data-id="element-206" data-mp="1njc78k9cnfg">
          <CtaButton data-id="element-207" data-mp="3svdyzatp0rfy">Get No-Queue Cricket</CtaButton>
        </div>
      </div>

      <PageLightbox pages={pages} index={lightboxIndex} onIndexChange={changeLightbox} onClose={closeLightbox} data-id="element-208" data-mp="28dv1a082ziqt" />
    </section>;
}